import React from 'react';
import { Target, Heart, Sparkles, Users, BookOpen, Lightbulb } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Curiosity',
      description: 'Encouraging children to ask questions, explore, and wonder about the world around them.'
    },
    {
      icon: Sparkles,
      title: 'Imagination',
      description: 'Inspiring creativity through whimsical stories and magical visual worlds.'
    },
    {
      icon: Lightbulb,
      title: 'Discovery',
      description: 'Cultivating a love for uncovering new facts, ideas, and exciting knowledge.'
    },
    {
      icon: Heart,
      title: 'Growth',
      description: 'Supporting children\'s cognitive, emotional, and moral development at every step.'
    },
    {
      icon: BookOpen,
      title: 'Adventure',
      description: 'Making learning a fun, daring, and imaginative activity that kids love.'
    },
    {
      icon: Users,
      title: 'Engagement',
      description: 'Ensuring every page turn is exciting, playful, and keeps young minds captivated.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Our Story
          </h1>
          <p className="text-2xl text-blue-100">
            Discover, Dream, Learn — One Adventure at a Time
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Our Mission</h2>
          </div>
          <Card className="border-2 shadow-xl">
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                At Learning Smart, we believe every child's curiosity is a magic key unlocking a world of learning. 
                Our books take young explorers on daring journeys through enchanted forests, shimmering oceans, and 
                hidden caves — all while weaving in vital lessons about science, nature, and morals.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mt-4">
                By disguising education as thrilling adventures, we ignite imaginations and inspire kids to discover, 
                think, and grow with each turn of the page. Because when learning feels like an exciting quest, children 
                are eager to explore the world around them — one adventure at a time.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600">
              The principles that guide every adventure we create
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value) => (
              <Card key={value.title} className="border-2 hover:shadow-xl transition-shadow group">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-4 group-hover:scale-110 transition-transform">
                    <value.icon className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">What Makes Us Different</h2>
          </div>
          <div className="space-y-8">
            <Card className="border-l-4 border-blue-600">
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Learning Disguised as Adventure</h3>
                <p className="text-gray-700">
                  We don't just add facts to stories — we weave education seamlessly into thrilling plots. 
                  Kids are so engaged with the adventure that they don't realize they're learning valuable lessons 
                  about science, nature, financial literacy, and more.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-purple-600">
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Whimsical & Captivating Illustrations</h3>
                <p className="text-gray-700">
                  Our vibrant, imaginative artwork brings every adventure to life. Each illustration is carefully 
                  crafted to spark wonder and keep young readers turning pages.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-green-600">
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Moral & Critical Thinking</h3>
                <p className="text-gray-700">
                  Beyond academic subjects, our books teach important life lessons. Characters face challenges that 
                  require problem-solving, empathy, and ethical decision-making.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Join Our Community
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Be part of a growing community of parents, educators, and young explorers who believe learning should be an adventure!
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;